/**
 * Requests made with `responseType: "blob"` (file downloads) still receive a
 * Blob body when the backend responds with a JSON error instead of a file —
 * axios does not auto-parse it. Read and JSON-parse it here so callers see
 * the real backend message instead of falling through to a generic one.
 */
const unwrapBlobErrorData = async (data) => {
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return data;
  try {
    return JSON.parse(await data.text());
  } catch {
    return data;
  }
};

/**
 * Utility to extract a clean error message from Axios/API responses
 * @param {Error} error - The error object from axios
 * @returns {Promise<string>} - An error message
 */
export const getErrorMessage = async (error) => {
  if (!error) return "An unexpected error occurred.";

  if (error.response) {
    const data = await unwrapBlobErrorData(error.response.data);

    // 1. Check for top-level message or title
    if (data?.message) return data.message;
    if (data?.title && !data?.errors) return data.title; // Use title if no specific errors exist

    // 2. Handle the "errors" object (like your example)
    if (data?.errors && typeof data.errors === "object") {
      // Get all values from the error object (e.g., [ ["Error 1"], ["Error 2"] ])
      const errorValues = Object.values(data.errors).flat();

      if (errorValues.length > 0) {
        return errorValues[0]; // Returns "Failed to read the request form..."
      }
    }

    // 3. Fallback to status text
    return `Error ${error.response.status}: ${error.response.statusText}`;
  }

  if (error.request) {
    return "Network error. Please check your internet connection.";
  }

  return error.message || "Something went wrong. Please try again.";
};
